import React from 'react'
import get from 'lodash/get'

import { Button, DownloadButton, Pagination, SelectInput, StatusLabel, Table, Text } from '@fielded/shared-ui'
import { SORT_DIRECTON } from '@fielded/shared-ui/src/utils/constants'
import { CheckmarkCircle, Clock } from '@fielded/shared-ui/src/icons'

import { formatDate } from '../../van-shared/utils/utils'
import { REPORT_STATUS, REPORT_STATUS_TO_LABEL, REPORT_STATUS_TO_STATUS_LABEL, VIEWS } from './utils'
import HomeStockReportsTableEmptyInfo from './HomeStockReportsTableEmptyInfo'

const REPORT_STATUS_TO_ICON = {
  [REPORT_STATUS.PROCESSING]: <Clock />,
  [REPORT_STATUS.COMPLETED]: <CheckmarkCircle />
}

const StockReportsTable = ({
  view,
  reports,
  branches,
  selectedBranch,
  loadingReports,
  loadingReport,
  downloadingReport,
  downloadedReport,
  onSelectBranch,
  onGenerateReport,
  onDownloadReport,
  totalReportsNumber,
  currentOffset,
  onPageChange,
  pageLimit
}) => {
  const isSingleView = view === VIEWS.SINGLE
  const isLoadingAllReports = loadingReports && !isSingleView

  const tableColumns = [
    {
      key: 'createdAt',
      header: 'Created on',
      dataKey: 'createdAt',
      headerClassName: 'home-stock-reports__table-header',
      cellRenderer: ({ cellData }) => formatDate(cellData, 'printShort')
    },
    {
      key: 'status',
      header: 'Status',
      dataKey: 'status',
      disableSort: true,
      cellRenderer: ({ cellData }) => (
        <StatusLabel
          className='home-stock-reports__table-status'
          label={REPORT_STATUS_TO_LABEL[cellData]}
          icon={REPORT_STATUS_TO_ICON[cellData]}
          status={REPORT_STATUS_TO_STATUS_LABEL[cellData]}
        />
      )
    },
    {
      key: 'file',
      header: 'Action',
      dataKey: 'file',
      disableSort: true,
      align: 'right',
      cellDataGetter: ({ rowData }) => rowData,
      cellRenderer: ({ cellData }) => {
        const createdAt = get(cellData, 'createdAt')
        const isDownloadingReport = downloadingReport && createdAt === downloadedReport.createdAt
        const status = get(cellData, 'status')
        const isProcessing = status === REPORT_STATUS.PROCESSING
        return (
          <DownloadButton
            loadingLabel='Download in progress'
            loading={isDownloadingReport}
            disabled={isProcessing}
            onClick={() => onDownloadReport(cellData)}
          >
            Download report
          </DownloadButton>
        )
      }
    }
  ]

  let info = 'Get a complete overview of stock status across all branches. Reports are prepared daily and generated when you download.'

  if (isSingleView) {
    info = 'Get detailed stock status for a specific branch. Select a branch to fetch data and generate your report.'
  }

  return (
    <section className='home-stock-reports__table home-stock-reports__section'>
      <Text className='home-stock-reports__table-info'>{info}</Text>
      {isSingleView && (
        <section className='home-stock-reports__table-inputs-wrapper'>
          <SelectInput
            className='home-stock-reports__table-select-input'
            placeholder='Search or select a branch'
            options={branches}
            onChange={option => onSelectBranch(option)}
            value={selectedBranch.branchId}
          />
          <Button
            colorVariant='brand'
            fill='full'
            loadingLabel='Fetching...'
            loading={loadingReport}
            disabled={!selectedBranch.branchId}
            onClick={onGenerateReport}
          >
            Fetch report
          </Button>
        </section>
      )}

      <section>
        <section className='home-stock-reports__table-wrapper'>
          <Table
            entries={isLoadingAllReports ? [] : reports}
            columns={tableColumns}
            initialSortBy='createdAt'
            initialSortDirection={SORT_DIRECTON.DESC}
            loading={isLoadingAllReports}
            noRowsContent={
              <HomeStockReportsTableEmptyInfo
                loading={isLoadingAllReports}
                isSingleView={isSingleView}
              />
            }
          />
        </section>

        {totalReportsNumber > pageLimit && (
          <div className='vs-u-margin-top'>
            <div className='vs-u-row vs-u-justify-end'>
              <div className='vs-u-row vs-u-gap vs-u-align-center'>
                <div>
                  {totalReportsNumber} reports
                </div>
                <Pagination
                  onChange={onPageChange}
                  currentOffset={currentOffset || 0}
                  totalItems={totalReportsNumber}
                  pageLimit={pageLimit}
                  isDisabled={loadingReports}
                />
              </div>
            </div>
          </div>
        )}
      </section>
    </section>
  )
}

export default StockReportsTable
